/**
 * FormatkaReorderList — wspólny wygląd + interakcja WIERSZY formatki w builderze:
 * uchwyt przeciągania (reorder) + inline ikonka oka (ukryj/pokaż), dla KAŻDEJ formatki
 * (adres macierzysty = pola; listy adresów/kontaktów/kont = kolumny).
 *
 * Dlaczego komponent, a nie inline w `element.render`: `render` elementu palety jest wołany
 * jak zwykła funkcja (bez trackowania hooków), a dnd-kit wymaga hooków (`useSortable`,
 * `DndContext`). Zwracając Z `render` ten KOMPONENT, React montuje go normalnie i hooki działają.
 *
 * Zapis: reorder → `onReorder(active, over)`, ukrycie → `onToggleHidden(slug)` — wołający
 * (element) mapuje to na własny klucz `settings` (`columnOrder`/`fieldOrder`,
 * `hiddenColumnIds`/`hiddenFields`). Kolejność wejściowa `items` MUSI być już rozwiązana
 * przez `applyOrder` (helper `formatka-order.ts`), żeby builder == karta.
 */

import type { ReactNode } from 'react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Eye, EyeOff, GripVertical } from 'lucide-react';

import { IconButton } from '@/components/IconButton';

export interface FormatkaReorderItem {
  slug: string;
  label: string;
  /** Podpis pod etykietą (np. „kolumna" / „tekst" / „słownik"); gdy ukryte → `hiddenLabel`. */
  hint: string;
}

export interface FormatkaReorderListProps {
  elementId: string;
  /** Wiersze W DOCELOWEJ KOLEJNOŚCI (po `applyOrder`). */
  items: FormatkaReorderItem[];
  hiddenSlugs: Set<string>;
  /** Ikonka oka (ukryj/pokaż) widoczna — builder z callbackiem zapisu. */
  canHide: boolean;
  /**
   * Uchwyt przeciągania (reorder) widoczny + aktywny DndContext. Na WŁASNYM layoucie
   * `true`, na SYSTEMOWYM `false` (zasada „systemowy = tylko ukrywanie, bez przesuwania").
   */
  canReorder: boolean;
  /**
   * @deprecated Oko ZAWSZE dostaje `pointerEvents:auto` (bezwarunkowo, jak `FieldBlock`),
   * więc ta flaga nie warunkuje już klikalności. Zachowana w API, bo wołający ją przekazują;
   * do usunięcia przy okazji. Na systemowym layoucie canvas jest inertny (`pointerEvents:none`)
   * i oko i tak przebija blokadę.
   */
  pierceLock?: boolean;
  onReorder: (activeSlug: string, overSlug: string) => void;
  onToggleHidden: (slug: string) => void;
  /** Nagłówek formatki. */
  header: ReactNode;
  hiddenLabel: string;
  labels: { hide: string; show: string; drag: string };
  /** `formatka-<x>` na kontenerze; wiersze: `<rowTestIdPrefix>-<slug>`. */
  testId: string;
  rowTestIdPrefix: string;
  elementType: string;
}

function SortableRow({
  item,
  isHidden,
  canHide,
  canReorder,
  onToggleHidden,
  hiddenLabel,
  labels,
  rowTestIdPrefix,
}: {
  item: FormatkaReorderItem;
  isHidden: boolean;
  canHide: boolean;
  canReorder: boolean;
  onToggleHidden: (slug: string) => void;
  hiddenLabel: string;
  labels: { hide: string; show: string; drag: string };
  rowTestIdPrefix: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.slug,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        'flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ' +
        (isHidden
          ? 'border-dashed border-border-secondary bg-bg-secondary opacity-60'
          : 'border-border-secondary bg-bg-secondary') +
        (isDragging ? ' z-10 opacity-70 shadow' : '')
      }
      data-testid={`${rowTestIdPrefix}-${item.slug}`}
      data-hidden={isHidden ? 'true' : 'false'}
    >
      <div className="flex min-w-0 items-center gap-2">
        {canReorder && (
          // eslint-disable-next-line no-restricted-syntax -- uchwyt dnd-kit wymaga surowego <button> (ref+listeners aktywatora); IconButton nie forwarduje aktywatora (jak istniejące uchwyty zakładek w SystemModuleBuilderPage)
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={labels.drag}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 cursor-grab rounded p-0.5 text-text-muted hover:bg-bg-tertiary"
            data-testid={`${rowTestIdPrefix}-${item.slug}-drag`}
          >
            <GripVertical className="h-3.5 w-3.5" aria-hidden={true} />
          </button>
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-text-primary">{item.label}</div>
          <div className="truncate text-xs text-text-muted">
            {isHidden ? hiddenLabel : item.hint}
          </div>
        </div>
      </div>
      {canHide && (
        // Oko ZAWSZE `pointerEvents:auto` (jak klocek pola `FieldBlock`): na systemowym
        // layoucie canvas jest inertny (pointerEvents:none) i oko musi przebić tę blokadę,
        // a na własnym layoucie rodzic i tak jest interaktywny — więc bezwarunkowo jest
        // bezpieczne i eliminuje `pierceLock` jako punkt awarii. (`pierceLock` zachowane w
        // API dla czytelności wywołań, ale nie warunkuje już klikalności.)
        <span
          className="shrink-0"
          style={{ pointerEvents: 'auto' }}
          data-testid={`${rowTestIdPrefix}-${item.slug}-eye`}
        >
          <IconButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onToggleHidden(item.slug);
            }}
            aria-label={isHidden ? labels.show : labels.hide}
          >
            {isHidden ? (
              <EyeOff className="h-4 w-4" aria-hidden={true} />
            ) : (
              <Eye className="h-4 w-4" aria-hidden={true} />
            )}
          </IconButton>
        </span>
      )}
    </div>
  );
}

export function FormatkaReorderList({
  items,
  hiddenSlugs,
  canHide,
  canReorder,
  onReorder,
  onToggleHidden,
  header,
  hiddenLabel,
  labels,
  testId,
  rowTestIdPrefix,
  elementType,
}: FormatkaReorderListProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  const rows = items.map((item) => (
    <SortableRow
      key={item.slug}
      item={item}
      isHidden={hiddenSlugs.has(item.slug)}
      canHide={canHide}
      canReorder={canReorder}
      onToggleHidden={onToggleHidden}
      hiddenLabel={hiddenLabel}
      labels={labels}
      rowTestIdPrefix={rowTestIdPrefix}
    />
  ));

  return (
    <div
      className="rounded-lg border border-border-secondary bg-bg-primary p-3"
      data-testid={testId}
      data-element-type={elementType}
    >
      {header}
      {canReorder ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.slug)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">{rows}</div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">{rows}</div>
      )}
    </div>
  );
}
